import { useCallback, useEffect, useState } from 'react';
import SumsubWebSdk from '@sumsub/websdk-react';
import type { AnyEventName, EventPayload } from '@sumsub/websdk';

// The SDK's onError payload. SnsError isn't re-exported from the package root,
// so we mirror its shape (code/error/reason) locally.
interface SnsError {
  code?: string;
  error?: string;
  reason?: string;
}
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { KycSdkSkeleton } from './KycSdkSkeleton';
import { KycSignupHandoff } from './KycSignupHandoff';
import { KycPreFlowPanel } from './panels/KycPreFlowPanel';
import { KycApprovedPanel } from './panels/KycApprovedPanel';
import { KycRejectedPanel } from './panels/KycRejectedPanel';
import { KycInProgressPanel } from './panels/KycInProgressPanel';
import { useAppAuth } from '@/lib/useAppAuth';
import { useKycAccessToken, useKycStatus } from '@/lib/kyc/useKyc';
import { useKycSupportAction } from '@/lib/kyc/useKycSupportAction';
import type { KycStatus } from '@/lib/kyc/types';
import { useKycActiveFlowKey, useKycOpenToken, useCloseKycGate, useApproveKycGate } from '@/store/kycGateStore';
import { useTheme } from '@/store';
import { clientErrorReporter } from '@/lib/realtime/client-error-reporter';

const GENERIC_LOAD_ERROR = "We couldn't start identity verification. Please try again.";
/** How often to poll KYC status while waiting on the webhook after a passing review. */
const STATUS_POLL_INTERVAL_MS = 3000;

/**
 * Sumsub WebSDK gate. Mount once in the app shell. Opens when something sets an
 * active flow on the KYC store — proactively, or via the 403 KYC_REQUIRED
 * backstop. The verification UI is rendered in a dismissible Dialog.
 *
 * Error handling is the point of this component. Three failure surfaces, each
 * recoverable without a page reload:
 *   1. Access-token mint fails  → error panel + Retry.
 *   2. Token expires mid-session → SDK calls expirationHandler; we re-mint. If
 *      the re-mint fails we flip to the error panel (SDK unmounts).
 *   3. The SDK itself errors (`onError`) → error panel + Retry, and we report
 *      it to the api for observability.
 *
 * The Sumsub SDK itself is launched ONLY for the pre-flow/verify path — never
 * for a REJECTED or ON_HOLD identity, which are terminal/held states with
 * their own branded pop-ups (`./panels`). See `View` below for the full state
 * machine, keyed on the resolved identity status for this flow.
 */
export function KycGate() {
  const flowKey = useKycActiveFlowKey();
  const openToken = useKycOpenToken();
  const close = useCloseKycGate();
  const approve = useApproveKycGate();
  const { user, isOnboarded } = useAppAuth();

  if (!flowKey) return null;
  // Signup completion comes before identity verification: a mid-signup account
  // (email unverified) is routed into the signup funnel instead of Sumsub. The
  // server enforces the same ordering (403 SIGNUP_INCOMPLETE on the token
  // mint), so this is UX, not the wall. `user` may still be loading (null) —
  // fall through to the runner and let the server gate decide.
  if (user && !isOnboarded) return <KycSignupHandoff onClose={close} />;
  // Remount the inner runner whenever a new open is requested so its token /
  // error state starts clean even if the same flow re-opens.
  return <KycRunner key={`${flowKey}:${openToken}`} flowKey={flowKey} onClose={close} onApproved={approve} />;
}

/**
 * What the gate shows, resolved from the identity status for this flow plus a
 * little local session state:
 *  - `loading`    — status not fetched yet.
 *  - `processing` — client-side GREEN received, backend hasn't confirmed yet.
 *  - `approved` / `onhold` / `rejected` — the branded result pop-ups. `onhold`
 *    and `approved` always win over everything else once resolved — the SDK
 *    is never (re)entered from either.
 *  - `preflow`    — NOT_STARTED / not-yet-submitted PENDING: the consent
 *    pop-up, shown before the SDK ever mints a token.
 *  - `sdk`        — the verify sub-flow (token mint → Sumsub iframe), entered
 *    only after an explicit "Continue" (preflow) or "Try Again" (rejected) tap.
 */
type View = 'loading' | 'processing' | 'approved' | 'onhold' | 'rejected' | 'preflow' | 'sdk';
/** Sub-phase once inside the `sdk` view (token mint → iframe). */
type SdkPhase = 'loading' | 'ready' | 'error';

function KycRunner({ flowKey, onClose, onApproved }: { flowKey: string; onClose: () => void; onApproved: () => void }) {
  // The SDK iframe defaults to the *device* theme — it can't see our
  // `data-theme` attribute — so a dark-OS user would get a dark Sumsub UI
  // inside the light app (and vice versa). Pass the app theme explicitly.
  // Applied at SDK launch only (the wrapper never re-inits on prop changes),
  // which is enough: KycRunner remounts on every gate open.
  const theme = useTheme();
  const openSupport = useKycSupportAction();
  // SDK-side error (onError / expired-refresh failure). Token-mint failure
  // comes from the query's isError instead — no mount effect, no state write
  // in an effect, so no cascading-render lint.
  const [sdkError, setSdkError] = useState<string | null>(null);
  // Sumsub returned a passing review (GREEN). The applicant is done on their
  // side, but our backend status may still be PENDING until Sumsub's webhook
  // lands — so we hold the dialog on a "finalizing" panel and poll the status
  // instead of closing out from under the user.
  const [reviewPassed, setReviewPassed] = useState(false);
  // The SDK has posted `idCheck.onReady` — its UI is rendered inside the
  // iframe. Until then we show our own skeleton over a collapsed (but
  // mounted) iframe, so the user never sees Sumsub's internal spinner.
  const [sdkReady, setSdkReady] = useState(false);
  // Set once the player explicitly opts into the verify flow — "Continue" on
  // the pre-flow consent pop-up, or "Try Again" on the rejected pop-up. Before
  // this, the SDK is never minted/rendered — the whole point of the gate.
  const [sdkLaunchRequested, setSdkLaunchRequested] = useState(false);
  const { data: statusData, refetch: refetchStatus } = useKycStatus(
    true,
    reviewPassed ? STATUS_POLL_INTERVAL_MS : undefined,
  );

  // The identity status resolved for this flow — NOT_STARTED when the backend
  // has no verification for it yet (mirrors `useKycIdentityPending`'s synthetic
  // default, scoped to this single flowKey rather than the HARD-enforcement set).
  const resolvedStatus: KycStatus = statusData?.verifications?.find((v) => v.flowKey === flowKey)?.status ?? 'NOT_STARTED';

  const launchSdk = useCallback(() => setSdkLaunchRequested(true), []);

  let view: View;
  if (statusData === undefined) {
    view = 'loading';
  } else if (resolvedStatus === 'APPROVED') {
    // Terminal states always win — checked before `reviewPassed` so a status
    // that has already resolved is never masked by a stale local flag.
    view = 'approved';
  } else if (resolvedStatus === 'ON_HOLD') {
    // Wins over `sdkLaunchRequested` unconditionally — a status flip to
    // ON_HOLD (e.g. mid-SDK, via the realtime status invalidation) must never
    // leave the SDK mounted or minting.
    view = 'onhold';
  } else if (resolvedStatus === 'REJECTED') {
    view = sdkLaunchRequested ? 'sdk' : 'rejected';
  } else if (reviewPassed) {
    // Client-side GREEN, backend hasn't confirmed a terminal outcome yet
    // (status may still read NOT_STARTED/PENDING) — hold on the finalizing
    // panel rather than re-entering the SDK or a pre-flow pop-up.
    view = 'processing';
  } else {
    // NOT_STARTED or genuinely-PENDING-not-yet-submitted.
    view = sdkLaunchRequested ? 'sdk' : 'preflow';
  }

  // Only mint a Sumsub token — and therefore only render the SDK iframe —
  // once the player has explicitly opted in (`view === 'sdk'`). Never for
  // REJECTED/ON_HOLD/APPROVED, and never before the pre-flow Continue tap.
  const mintEnabled = view === 'sdk';
  const { data, isError, isFetching, refetch } = useKycAccessToken(mintEnabled ? flowKey : null);

  const token = data?.token ?? null;
  const sdkPhase: SdkPhase = sdkError || isError ? 'error' : token ? 'ready' : 'loading';
  const errorMessage = sdkError ?? GENERIC_LOAD_ERROR;

  // Report a token-mint failure for observability (no state write → effect-safe).
  useEffect(() => {
    if (isError) {
      clientErrorReporter.report({ kind: 'kyc-token-failed', detail: { flowKey } });
    }
  }, [isError, flowKey]);

  const retry = useCallback(() => {
    setSdkError(null);
    void refetch();
  }, [refetch]);

  // On expiry the SDK asks for a fresh token. Re-mint via refetch; throw if it
  // fails so the SDK surfaces its own expiry state (we also flip to error).
  const expirationHandler = useCallback(async (): Promise<string> => {
    const result = await refetch();
    const fresh = result.data?.token;
    if (!fresh) {
      setSdkError(GENERIC_LOAD_ERROR);
      throw new Error('failed to refresh KYC token');
    }
    return fresh;
  }, [refetch]);

  const handleError = useCallback(
    (error: SnsError) => {
      if (error?.reason === 'permissionsDenied') {
        return;
      }
      clientErrorReporter.report({
        kind: 'kyc-websdk-error',
        detail: { flowKey, code: error?.code, error: error?.error, reason: error?.reason },
      });
    },
    [flowKey],
  );

  const handleMessage = useCallback(
    (type: AnyEventName, payload: EventPayload<AnyEventName>) => {
      if (type === 'idCheck.onReady') {
        setSdkReady(true);
        return;
      }
      if (type === 'idCheck.onApplicantStatusChanged' || type === 'idCheck.applicantReviewComplete') {
        // Server state is authoritative — pull fresh status either way.
        void refetchStatus();
        const reviewAnswer = (payload as { reviewResult?: { reviewAnswer?: string } })?.reviewResult?.reviewAnswer;
        if (reviewAnswer === 'GREEN') setReviewPassed(true);
      }
    },
    [refetchStatus],
  );

  if (view === 'loading') return null;

  if (view === 'approved') return <KycApprovedPanel onClose={onClose} onApproved={onApproved} />;
  if (view === 'onhold') return <KycInProgressPanel onClose={onClose} onContactSupport={openSupport} />;
  if (view === 'rejected') return <KycRejectedPanel onClose={onClose} onContactSupport={openSupport} />;
  if (view === 'preflow') return <KycPreFlowPanel onClose={onClose} onContinue={launchSdk} />;
  // The transient finalizing state (client-side GREEN, backend webhook not yet
  // landed) shows the same branded "Verification in Progress" pop-up as ON_HOLD.
  // The status poll keeps running underneath — if the backend resolves before
  // the player taps "Got It", the runner re-renders into the approved/on-hold/
  // rejected panel; otherwise "Got It" dismisses and the notify-you copy holds.
  if (view === 'processing')
    return <KycInProgressPanel onClose={onClose} onContactSupport={openSupport} testIdBase="kyc-processing" />;

  // view === 'sdk': keep the dialog closed until the access-token request
  // resolves — on open we mint the token in the background and only surface the
  // modal once there is something actionable to show (the SDK iframe or a
  // retryable error). This avoids flashing an empty "Preparing…" modal before
  // the api has confirmed the flow.
  if (view === 'sdk' && sdkPhase === 'loading') return null;

  return (
    <Dialog open onClose={onClose} size="md">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">Identity verification</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-primary">
            ✕
          </button>
        </div>

        {view === 'sdk' && sdkPhase === 'error' && (
          <div className="flex flex-col gap-3 py-4" role="alert" data-testid="kyc-error">
            <p className="text-sm text-danger-alt">{errorMessage}</p>
            <div className="flex gap-2">
              <Button onClick={retry} disabled={isFetching}>
                {isFetching ? 'Retrying…' : 'Try again'}
              </Button>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {view === 'sdk' && sdkPhase === 'ready' && token && (
          <>
            {!sdkReady && <KycSdkSkeleton />}
            {/* Negative margins cancel the Dialog's p-6/p-8 so the iframe runs
                edge-to-edge — one visual container instead of a box-in-a-box
                (the header above keeps its padding). Bottom rounding + clip keep
                the dialog's rounded corners over the square iframe.
                Until `idCheck.onReady` the host is collapsed (invisible + max-h-0,
                not display:none, so the iframe still boots and measures) and the
                skeleton above stands in for Sumsub's internal spinner. */}
            <div
              data-testid="kyc-sdk-host"
              className={cn(
                'overflow-y-auto [-webkit-overflow-scrolling:touch] overscroll-contain max-h-[min(800px,80vh)]',
                // -mt-4 cancels the parent's gap-4 so the collapsed host is zero-footprint.
                sdkReady ? '-mx-6 -mb-6 rounded-b-card lg:-mx-8 lg:-mb-8' : 'invisible max-h-0 -mt-4',
              )}
            >
              <SumsubWebSdk
                accessToken={token}
                expirationHandler={expirationHandler}
                onMessage={handleMessage}
                onError={handleError}
                config={{ theme }}
                options={{ addViewportTag: false, adaptIframeHeight: true }}
              />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
